// src/pages/seeker/SeekerLanguages.jsx

import React, { useEffect, useState } from 'react';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { FieldError, useConfirm } from '../../components/shared/UI';

const C = {
  navy: '#0e4e95', white: '#ffffff',
  offwhite: '#f4f7fb', text: '#1a1a1a', muted: '#666',
  border: '#d0daea',
};

// Backend proficiency values + display labels
const LEVELS = [
  { value: 'native',         label: 'Native'         },
  { value: 'fluent',         label: 'Fluent'         },
  { value: 'advanced',       label: 'Advanced'       },
  { value: 'professional',   label: 'Professional'   },
  { value: 'intermediate',   label: 'Intermediate'   },
  { value: 'conversational', label: 'Conversational' },
  { value: 'basic',          label: 'Basic'          },
];

const levelLabel = (val) => LEVELS.find(l => l.value === val?.toLowerCase())?.label || val || '';

export const SeekerLanguages = () => {
  const { confirm, dialog } = useConfirm();
  const [items,      setItems]      = useState([]);
  const [name,       setName]       = useState('');
  const [level,      setLevel]      = useState('');
  const [loading,    setLoading]    = useState(true);
  const [adding,     setAdding]     = useState(false);
  const [levelError, setLevelError] = useState('');

  const load = async () => {
    try {
      const res = await seekerAPI.getLanguages();
      setItems(res.data.data || []);
    } catch { toast.error('Failed to load languages'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    if (!level) { setLevelError('Please select a proficiency level'); return; }
    setLevelError('');
    setAdding(true);
    try {
      await seekerAPI.addLanguage({ language: name.trim(), proficiency: level });
      setName(''); setLevel('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add language');
    } finally { setAdding(false); }
  };

  const remove = async (item) => {
    let affectedCVs = [];
    try {
      const res = await seekerAPI.getCVProfiles();
      const cvs = res.data.data || [];
      affectedCVs = cvs.filter(cv => {
        const langs = cv.sectionConfig?.cvLanguages || [];
        return langs.some(l => l.toLowerCase() === item.language.toLowerCase());
      });
    } catch { /* ignore — proceed without CV info */ }

    const lines = [`Remove "${item.language}" from your profile?`];
    if (affectedCVs.length > 0) {
      const cvList = affectedCVs.map(cv => `"${cv.title}"`).join(', ');
      lines.push('', `It is currently included in ${affectedCVs.length === 1 ? 'CV' : 'CVs'}: ${cvList}.`);
      lines.push('', 'It will be removed from all of the above. Continue?');
    }
    if (!await confirm('Remove Language', lines.join('\n'))) return;

    try {
      await seekerAPI.removeLanguage(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch { toast.error('Failed to remove language'); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div className="fade-in">
      {dialog}
      <div className="page-header">
        <h1>Languages</h1>
        <p>Languages you speak — used across all your CVs</p>
      </div>

      <div className="page-content">
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '20px 24px' }}>

          <div className="form-row" style={{ alignItems: 'end', marginBottom: '20px' }}>
            <div>
              <label className="form-label">Language<span style={{ color: '#CC0000', marginLeft: '2px' }}>*</span></label>
              <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder="e.g. English" className="form-input" />
            </div>
            <div>
              <label className="form-label">Proficiency<span style={{ color: '#CC0000', marginLeft: '2px' }}>*</span></label>
              <select value={level} onChange={e => { setLevel(e.target.value); setLevelError(''); }} className="form-input" style={{ background: 'white' }}>
                <option value="">— Select —</option>
                {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
              {levelError && <FieldError>{levelError}</FieldError>}
            </div>
            <button onClick={add} disabled={adding} className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
              {adding ? '…' : '+ Add'}
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {items.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0' }}>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: C.text }}>{item.language}</span>
                <span style={{ fontSize: '12px', color: C.muted, background: C.offwhite, padding: '2px 10px', borderRadius: '20px', border: `1px solid ${C.border}` }}>{levelLabel(item.proficiency)}</span>
                <button onClick={() => remove(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', display: 'flex' }}>
                  <X size={14} />
                </button>
              </div>
            ))}
            {items.length === 0 && <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>No languages added yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
